import { useEffect, useRef } from "react";
import type { JSX } from "react";
import { Button } from "./ui/button";
import { useI18n } from "../i18n";

export interface ShowMapConfirmProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description: string;
  readonly onConfirm: () => void;
  readonly onCancel?: () => void;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  /**
   * GK-5: the players' characters stand on their map now (N > 0). Instead of one «Show» the GM answers with a button:
   * «Show with the characters (N)» (main) or «Map only» (onConfirm). Not a checkbox: a yes/no question answered with
   * «Show» left the characters behind (owner, 25.10).
   */
  readonly carry?: { readonly count: number; readonly confirmWithParty: () => void } | undefined;
}

/**
 * The confirmation of «Show to players…» (mockup `.confirm`): a card under the strip, no dimming of the
 * screen — the GM still sees the map he is about to show. The main button first, a neutral «Cancel» after it.
 * Not modal: Escape and «Cancel» close it, and nothing is sent before «Show». The other confirmations of the
 * app keep ConfirmDialog.
 */
export default function ShowMapConfirm({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  onCancel,
  confirmLabel,
  cancelLabel,
  carry,
}: ShowMapConfirmProps): JSX.Element | null {
  const { t } = useI18n();
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (open) confirmRef.current?.focus();
  }, [open]);
  if (!open) return null;
  const cancel = (): void => {
    onCancel?.();
    onOpenChange(false);
  };
  const confirm = (): void => {
    onConfirm();
    onOpenChange(false);
  };
  const confirmWithParty = (): void => {
    carry?.confirmWithParty();
    onOpenChange(false);
  };
  return (
    <div
      role="alertdialog"
      aria-labelledby="show-map-confirm-title"
      aria-describedby="show-map-confirm-body"
      data-testid="confirm-dialog"
      className="fixed left-4 top-24 z-50 flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2 rounded-md border border-line bg-panel-2 px-2.5 py-2 shadow-xl"
      onKeyDown={(e): void => {
        if (e.key === "Escape") {
          e.stopPropagation();
          cancel();
        }
      }}
    >
      <h2 id="show-map-confirm-title" className="text-ui font-semibold text-fg">{title}</h2>
      <p id="show-map-confirm-body" className="text-meta leading-relaxed text-muted-foreground">{description}</p>
      <div className="flex flex-wrap items-center gap-2">
        {carry !== undefined ? (
          <>
            <Button ref={confirmRef} size="sm" data-testid="show-map-with-party" onClick={confirmWithParty}>
              {t("mapView.showWithParty").replace("{n}", String(carry.count))}
            </Button>
            <Button size="sm" variant="outline" className="border-line bg-panel-2/80 text-fg hover:bg-line" data-testid="confirm-dialog-confirm" onClick={confirm}>
              {t("mapView.showMapOnly")}
            </Button>
          </>
        ) : (
          <Button ref={confirmRef} size="sm" data-testid="confirm-dialog-confirm" onClick={confirm}>
            {confirmLabel ?? t("confirmDialog.confirm")}
          </Button>
        )}
        <Button size="sm" variant="outline" className="border-line bg-panel-2/80 text-fg hover:bg-line" data-testid="confirm-dialog-cancel" onClick={cancel}>
          {cancelLabel ?? t("confirmDialog.cancel")}
        </Button>
      </div>
    </div>
  );
}
